import type {
	UnclassifiedAlert,
	UnclassifiedSuggestion,
} from "@sitescene/types";

import { useNavigate } from "react-router";

import { Badge } from "@/components/badge";
import { type Column, DataTable } from "@/components/data-table";
import { Button } from "@/components/field";
import { StatusMessage } from "@/components/status-message";
import { plausibilityVerificationPath, ROUTES } from "@/constants/routes";
import { useOrgSelection } from "@/context/use-org-selection";
import {
	useIgnoreUnclassified,
	useRegenerateSuggestion,
	useUnclassifiedAlerts,
} from "@/features/plausibility/use-unclassified";

import { formatWhen } from "./format";

/**
 * Alerts no alertable class explained, across every organization. The
 * verifier still checked each one's footage against Stage 1's own
 * description; this is where staff see what the taxonomy is missing and
 * create the class the alert needed. Each row carries a model's suggestion
 * for that step — a new class, a wider description for an existing one, or
 * nothing — which staff review in the taxonomy editor before it is saved.
 */
export const UnclassifiedAlerts = () => {
	const alerts = useUnclassifiedAlerts("pending");
	const ignore = useIgnoreUnclassified();
	const regenerate = useRegenerateSuggestion();
	const { organizations, selectOrg } = useOrgSelection();
	const navigate = useNavigate();
	const rows = alerts.data ?? [];

	const organizationOf = (row: UnclassifiedAlert) =>
		organizations.find((org) => org.tenantId === row.tenantId);

	const columns: Column<UnclassifiedAlert>[] = [
		{
			header: "When",
			key: "when",
			render: (row) => (
				<span className="text-secondary text-xs">
					{formatWhen(row.capturedAt)}
				</span>
			),
		},
		{
			header: "Organization",
			key: "org",
			render: (row) => organizationOf(row)?.name ?? row.tenantId,
		},
		{
			header: "Stage 1",
			key: "stage1",
			render: (row) => (
				<div className="max-w-xl">
					<div className="text-primary text-xs">
						<code>{row.type}</code> · severity {row.severity}
						{row.labels.length > 0 && ` · ${row.labels.join(", ")}`}
					</div>
					{row.description && (
						<div className="text-secondary text-xs">{row.description}</div>
					)}
				</div>
			),
		},
		{
			header: "Suggestion",
			key: "suggestion",
			render: (row) => <SuggestionSummary suggestion={row.suggestion} />,
		},
		{
			header: "Footage check",
			key: "decision",
			render: (row) => (
				<span className="text-secondary text-xs">{row.decision ?? "—"}</span>
			),
		},
		{
			header: "",
			key: "actions",
			render: (row) => {
				const org = organizationOf(row);
				return (
					<div className="flex flex-wrap gap-2">
						<Button
							disabled={!org}
							onClick={() => {
								if (!org) return;
								selectOrg(org.orgCode);
								void navigate(plausibilityVerificationPath(row.verificationId));
							}}
							title={
								org ? undefined : "You have no access to this organization"
							}
							type="button"
							variant="secondary"
						>
							Open verification
						</Button>
						<SuggestedAction
							alertId={row.id}
							onNavigate={(search) =>
								void navigate(`${ROUTES.GLOBAL_TAXONOMY}?${search}`)
							}
							suggestion={row.suggestion}
						/>
						{row.suggestion?.kind !== "newClass" && (
							<Button
								onClick={() =>
									void navigate(
										`${ROUTES.GLOBAL_TAXONOMY}?${new URLSearchParams({
											description: row.description ?? "",
											from: row.id,
											name: humanize(row.type),
											new: "1",
										}).toString()}`,
									)
								}
								type="button"
								variant="secondary"
							>
								Create class
							</Button>
						)}
						<Button
							disabled={regenerate.isPending}
							onClick={() => regenerate.mutate(row.id)}
							type="button"
							variant="secondary"
						>
							{row.suggestion ? "Regenerate" : "Suggest"}
						</Button>
						<Button
							disabled={ignore.isPending}
							onClick={() => ignore.mutate([row.id])}
							type="button"
							variant="danger"
						>
							Ignore
						</Button>
					</div>
				);
			},
		},
	];

	return (
		<div>
			<h2 className="text-primary text-base font-semibold">
				Unclassified alerts
			</h2>
			<p className="text-secondary text-sm">
				Alerts no alertable class explained. Their footage was still checked,
				against Stage 1's own description. Each has a suggested next step:
				create a new class, widen the description of the class that should have
				caught it, or ignore it. Suggestions are reviewed in the taxonomy editor
				before anything is saved.
			</p>
			{(ignore.error ?? regenerate.error) && (
				<p className="text-error-primary mt-2 text-sm" role="alert">
					{(ignore.error ?? regenerate.error)?.message}
				</p>
			)}
			<div className="mt-4">
				<StatusMessage
					empty="No unclassified alerts. Every recent alert matched a class."
					error={alerts.error}
					isEmpty={!alerts.isLoading && rows.length === 0}
					isLoading={alerts.isLoading}
					loading="Loading unclassified alerts…"
				/>
				{rows.length > 0 && (
					<DataTable columns={columns} rowKey={(row) => row.id} rows={rows} />
				)}
			</div>
		</div>
	);
};

const SUGGESTION_BADGES: Record<
	UnclassifiedSuggestion["kind"],
	{ label: string; tone: string }
> = {
	existingClass: { label: "existing class", tone: "reviewQueue" },
	failed: { label: "failed", tone: "failed" },
	newClass: { label: "new class", tone: "alertNow" },
	noAction: { label: "ignore", tone: "noChange" },
};

const SuggestionSummary = ({
	suggestion,
}: {
	suggestion: UnclassifiedSuggestion | undefined;
}) => {
	if (!suggestion) {
		return <span className="text-tertiary text-xs">None yet</span>;
	}
	const badge = SUGGESTION_BADGES[suggestion.kind];
	return (
		<div className="flex max-w-sm flex-col gap-1 text-xs">
			<div className="flex flex-wrap items-center gap-1">
				<Badge label={badge.label} value={badge.tone} />
				{suggestion.kind === "newClass" && (
					<>
						<code>{suggestion.classId}</code>
						<span className="text-tertiary">
							{suggestion.features.length} features
						</span>
					</>
				)}
				{suggestion.kind === "existingClass" && (
					<code>→ {suggestion.classId}</code>
				)}
			</div>
			<span className="text-secondary">
				{suggestion.kind === "failed"
					? suggestion.reason
					: suggestion.reasoning}
			</span>
		</div>
	);
};

/** The button that takes a suggestion into the taxonomy editor for review. */
const SuggestedAction = ({
	alertId,
	onNavigate,
	suggestion,
}: {
	alertId: string;
	onNavigate: (search: string) => void;
	suggestion: UnclassifiedSuggestion | undefined;
}) => {
	if (suggestion?.kind === "newClass") {
		return (
			<Button
				onClick={() =>
					onNavigate(
						new URLSearchParams({ from: alertId, new: "1" }).toString(),
					)
				}
				type="button"
			>
				Create suggested class
			</Button>
		);
	}
	if (suggestion?.kind === "existingClass") {
		return (
			<Button
				onClick={() =>
					onNavigate(
						new URLSearchParams({
							edit: suggestion.classId,
							from: alertId,
						}).toString(),
					)
				}
				type="button"
			>
				Update {suggestion.classId}
			</Button>
		);
	}
	return null;
};

/** `construction_work` → `Construction work`, as a starting display name. */
function humanize(type: string): string {
	const words = type.replace(/[_-]+/g, " ").trim();
	return words ? words[0]!.toUpperCase() + words.slice(1) : "";
}
